// frontend/src/components/dashboard/pos/components/CategoriesSidebar.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Category,
  AutoAwesome,
  // Import all icons used in CategoryList for consistency
  Fastfood,
  LocalDrink,
  Cake,
  Coffee,
  RestaurantMenu,
  LocalBar,
  EmojiFoodBeverage,
  Icecream,
  LocalPizza,
  LocalDining,
  RamenDining,
  SetMeal,
  LunchDining,
  Restaurant,
  Liquor,
  SoupKitchen,
  BreakfastDining,
  BakeryDining,
  DinnerDining,
  EggAlt,
  KebabDining,
  SportsBar
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const CategoriesSidebar = ({
  categories = [],
  selectedCategory,
  onCategoryChange,
  loading,
  isMobile,
  deviceInfo,
  layoutConfig
}) => {

  useEffect(() => {
    if (categories.length > 0) {
      console.log('🧪 VERIFICATION TEST - Categories received:', categories);
      
      let workingCount = 0;
      let brokenCount = 0;
      
      categories.forEach((cat, index) => {
        const hasIcon = cat.icon !== undefined && cat.icon !== null;
        const hasColor = (cat.colorValue !== undefined && cat.colorValue !== null) || 
                        (cat.color_value !== undefined && cat.color_value !== null);
        
        const status = hasIcon && hasColor ? '✅ WORKING' : '❌ BROKEN';
        
        if (hasIcon && hasColor) {
          workingCount++;
        } else {
          brokenCount++;
        }
        
        console.log(`🧪 Category ${index + 1} "${cat.name}": ${status}`, {
          hasIcon: hasIcon,
          hasColor: hasColor,
          icon: cat.icon,
          colorValue: cat.colorValue || cat.color_value,
          colorName: cat.colorName || cat.color_name,
          fullCategory: cat
        });
      });
      
      console.log(`🧪 VERIFICATION SUMMARY:`);
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.log(`   Total categories: ${categories.length}`);
      console.log(`   ✅ Working (with icon + color): ${workingCount}`);
      console.log(`   ❌ Broken (missing data): ${brokenCount}`);
      
      if (workingCount === categories.length) {
        console.log('🎉 SUCCESS! All categories have icon and color data!');
        console.log('🎨 Colors should now display correctly in CategoriesSidebar');
      } else {
        console.log('⚠️ Some categories still missing data.');
        console.log('💡 Next steps:');
        console.log('   1. Verify backend server was restarted');
        console.log('   2. Check getItemsByBranchId function was updated');
        console.log('   3. Or use the frontend fix provided');
      }
    }
  }, [categories]);

  const { t } = useTranslation();
  const theme = useTheme();
  const MotionDiv = motion.div;
  const MotionBox = motion(Box);
  
  const [hoveredCategory, setHoveredCategory] = useState(null);
  
  // Responsive breakpoints
  const isTabletSmall = deviceInfo?.screenSize === 'small';

  // ✅ FIXED: Use the EXACT same icon mapping as CategoryList
  const getIconByName = (iconName) => {
    if (!iconName) return <Category />;
    
    // ✅ IDENTICAL mapping to CategoryList.jsx
    const iconMap = {
      'Fastfood': Fastfood,
      'Drinks': LocalDrink,
      'Dessert': Cake,
      'Coffee': Coffee,
      'Menu': RestaurantMenu,
      'Bar': LocalBar,
      'Tea': EmojiFoodBeverage,
      'Ice Cream': Icecream,
      'Pizza': LocalPizza,
      'Dining': LocalDining,
      'Ramen': RamenDining,
      'Sushi': SetMeal,
      'Lunch': LunchDining,
      'Restaurant': Restaurant,
      'Wine': Liquor,
      'Soup': SoupKitchen,
      'Breakfast': BreakfastDining,
      'Bakery': BakeryDining,
      'Dinner': DinnerDining,
      'Egg': EggAlt,
      'Kebab': KebabDining,
      'Tapas': SportsBar
    };
    
    // Get the component from our map
    const IconComponent = iconMap[iconName];
    
    if (IconComponent) {
      return <IconComponent fontSize="small" sx={{ fontSize: isTabletSmall ? 16 : 18 }} />;
    }
    
    // Fallback for icons that don't exist
    return <Category fontSize="small" sx={{ fontSize: isTabletSmall ? 16 : 18 }} />;
  };

  // ✅ DEBUG: Enhanced logging to trace data issues
  useEffect(() => {
    if (categories && categories.length > 0) {
      console.log('🔍 CategoriesSidebar - Categories received:', categories);
      
      categories.forEach((category, index) => {
        console.log(`📂 Category ${index + 1} analysis:`, {
          id: category.category_id || category.id,
          name: category.name || category.category_name,
          icon: category.icon,
          colorValue: category.colorValue,
          color_value: category.color_value,
          colorName: category.colorName,
          color_name: category.color_name,
          fullCategory: category
        });
      });
    }
  }, [categories]);

  // Format category name with enhanced styling
  const formatCategoryName = (name) => {
    if (!name || typeof name !== 'string') {
      return (
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: isTabletSmall ? '0.6rem' : '0.7rem',
            textAlign: 'center',
            lineHeight: 1.1,
            fontWeight: 500,
            color: 'text.secondary'
          }}
        >
          Unknown
        </Typography>
      );
    }

    const words = name.split(' ');
    if (words.length > 1 && name.length > 8) {
      return (
        <Box sx={{ textAlign: 'center', lineHeight: 1.1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: isTabletSmall ? '0.6rem' : '0.7rem',
              fontWeight: 500,
              display: 'block'
            }}
          >
            {words[0]}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: isTabletSmall ? '0.6rem' : '0.7rem',
              fontWeight: 500,
              display: 'block'
            }}
          >
            {words.slice(1).join(' ')}
          </Typography>
        </Box>
      );
    }
    
    return (
      <Typography 
        variant="caption" 
        sx={{ 
          fontSize: isTabletSmall ? '0.6rem' : '0.7rem',
          textAlign: 'center',
          lineHeight: 1.1,
          fontWeight: 500,
          display: 'block'
        }}
      >
        {name}
      </Typography>
    );
  };

  // Don't show sidebar on mobile
  if (isMobile) {
    return null;
  }

  // Get responsive width from layout config
  const categoriesWidth = layoutConfig?.categoriesWidth || 120;

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: categoriesWidth, 
        borderRight: `1px solid ${theme.palette.divider}`,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        position: 'relative',
        flexShrink: 0,
        background: 'linear-gradient(180deg, #fafafa 0%, #f8fafc 50%, #f1f5f9 100%)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(102, 126, 234, 0.04) 0%, transparent 60%), radial-gradient(circle at 70% 80%, rgba(118, 75, 162, 0.03) 0%, transparent 60%)',
          pointerEvents: 'none',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '2px',
          height: '100%',
          background: 'linear-gradient(180deg, transparent 0%, rgba(102, 126, 234, 0.2) 20%, rgba(102, 126, 234, 0.4) 50%, rgba(102, 126, 234, 0.2) 80%, transparent 100%)',
          zIndex: 1
        }
      }}
    >
      {/* ✅ FIXED: Completely Static Header - No Animations */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 50%, rgba(102, 126, 234, 0.06) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(102, 126, 234, 0.15)',
          p: isTabletSmall ? 1.5 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.08)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%)',
          }
        }}
      >
        {/* ✅ FIXED: Completely Static Icon - No Animations */}
        {/* <AutoAwesome sx={{ 
          fontSize: isTabletSmall ? 16 : 20, 
          color: 'primary.main',
          filter: 'drop-shadow(0 2px 4px rgba(102, 126, 234, 0.3))'
        }} /> */}
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 700,
            fontSize: isTabletSmall ? '0.7rem' : '0.8rem',
            color: 'primary.main',
            letterSpacing: 1,
            textTransform: 'uppercase'
          }}
        >
          {t('pos.categories')}
        </Typography>
      </Box>

      {/* Main Content with Enhanced Scrolling */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          position: 'relative',
          zIndex: 1
        }}>
          <MotionDiv
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <CircularProgress 
              size={isTabletSmall ? 24 : 32} 
              sx={{ 
                color: 'primary.main',
                filter: 'drop-shadow(0 2px 8px rgba(102, 126, 234, 0.3))'
              }} 
            />
          </MotionDiv>
        </Box>
      ) : (
        <Box
          id="category-list"
          sx={{ 
            overflowY: 'auto',
            flexGrow: 1,
            pt: isTabletSmall ? 2 : 2.5,
            pb: isTabletSmall ? 2 : 2.5,
            px: isTabletSmall ? 0.75 : 1.25,
            position: 'relative',
            zIndex: 1,
            '&::-webkit-scrollbar': {
              width: '3px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, rgba(102, 126, 234, 0.4), rgba(118, 75, 162, 0.4))',
              borderRadius: '3px',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(180deg, rgba(102, 126, 234, 0.6), rgba(118, 75, 162, 0.6))'
              }
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <List sx={{ p: 0 }}>
            {/* All Categories with Premium Styling */}
            <MotionDiv
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ 
                duration: 0.4, 
                delay: 0.1,
                type: "spring",
                stiffness: 120,
                damping: 12
              }}
            >
              <Tooltip 
                title={t('pos.allCategories')} 
                placement="right"
                arrow
                TransitionComponent={Fade}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'rgba(0, 0, 0, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }
                  },
                  arrow: {
                    sx: {
                      color: 'rgba(0, 0, 0, 0.9)',
                    }
                  }
                }}
              >
                <ListItem 
                  component="div" 
                  selected={selectedCategory === 'all'}
                  onClick={() => onCategoryChange('all')}
                  onMouseEnter={() => setHoveredCategory('all')}
                  onMouseLeave={() => setHoveredCategory(null)}
                  sx={{ 
                    borderRadius: 3,
                    mb: isTabletSmall ? 0.75 : 1.25,
                    flexDirection: 'column',
                    p: isTabletSmall ? 1 : 1.5,
                    cursor: 'pointer',
                    minHeight: isTabletSmall ? 60 : 75,
                    position: 'relative',
                    overflow: 'hidden',
                    background: selectedCategory === 'all' 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.15) 100%)'
                      : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: selectedCategory === 'all'
                      ? '2px solid rgba(102, 126, 234, 0.4)'
                      : '1px solid rgba(0, 0, 0, 0.06)',
                    boxShadow: selectedCategory === 'all'
                      ? '0 8px 32px rgba(102, 126, 234, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                      : hoveredCategory === 'all'
                        ? '0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                        : '0 4px 12px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-3px) scale(1.02)',
                      background: selectedCategory === 'all'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.2) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%)',
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                      boxShadow: '0 12px 40px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                    },
                    '&:active': {
                      transform: 'translateY(-1px) scale(0.98)'
                    },
                    '&::before': selectedCategory === 'all' ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '3px',
                      background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
                      opacity: 0.9
                    } : {},
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '200%',
                      height: '200%',
                      background: 'radial-gradient(circle, rgba(102, 126, 234, 0.05) 0%, transparent 70%)',
                      opacity: hoveredCategory === 'all' ? 1 : 0,
                      transition: 'opacity 0.3s ease',
                      pointerEvents: 'none'
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 'auto', mb: 0.75 }}>
                    <MotionDiv
                      whileHover={{ scale: 1.15, rotate: 8 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ type: "spring", stiffness: 200, damping: 10 }}
                    >
                      <Avatar 
                        sx={{ 
                          bgcolor: '#667eea', // ✅ Fixed primary color for "All Categories"
                          width: isTabletSmall ? 28 : 36,
                          height: isTabletSmall ? 28 : 36,
                          border: selectedCategory === 'all'
                            ? '2px solid rgba(255, 255, 255, 0.6)'
                            : '1px solid rgba(102, 126, 234, 0.3)',
                          boxShadow: selectedCategory === 'all'
                            ? '0 8px 24px rgba(102, 126, 234, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                            : '0 4px 16px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
                            borderRadius: '50%',
                            pointerEvents: 'none'
                          }
                        }}
                      >
                        <Category fontSize="small" sx={{ 
                          fontSize: isTabletSmall ? 14 : 18,
                          color: 'white',
                          filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
                        }} />
                      </Avatar>
                    </MotionDiv>
                  </ListItemAvatar>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      textAlign: 'center',
                      fontSize: isTabletSmall ? '0.65rem' : '0.75rem',
                      lineHeight: 1.2,
                      fontWeight: selectedCategory === 'all' ? 700 : 600,
                      color: selectedCategory === 'all' ? 'primary.main' : 'text.primary',
                      textShadow: selectedCategory === 'all' ? '0 1px 2px rgba(102, 126, 234, 0.3)' : 'none',
                      background: selectedCategory === 'all' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'none',
                      backgroundClip: selectedCategory === 'all' ? 'text' : 'none',
                      WebkitBackgroundClip: selectedCategory === 'all' ? 'text' : 'none',
                      WebkitTextFillColor: selectedCategory === 'all' ? 'transparent' : 'inherit'
                    }}
                  >
                    {t('pos.allCategories')}
                  </Typography>
                </ListItem>
              </Tooltip>
            </MotionDiv>
            
            {/* Enhanced Category Items with Premium Styling */}
            <AnimatePresence>
              {Array.isArray(categories) && categories
                .filter(category => category && (category.category_id || category.id))
                .map((category, index) => {
                  const categoryId = category.category_id || category.id;
                  const categoryName = category.name || category.category_name || 'Unknown Category';
                  
                  // ✅ FIXED: Proper color extraction with multiple fallbacks
                  const categoryColor = (() => {
                    const colorValue = category.colorValue || category.color_value;
                    const colorName = category.colorName || category.color_name;
                    
                    // First try direct color value
                    if (colorValue) {
                      return colorValue;
                    }
                    
                    // Then try to map color name to actual color value
                    if (colorName) {
                      const colorMap = {
                        'Red': '#f44336',
                        'Pink': '#e91e63',
                        'Purple': '#9c27b0',
                        'Deep Purple': '#673ab7',
                        'Indigo': '#3f51b5',
                        'Blue': '#2196f3',
                        'Light Blue': '#03a9f4',
                        'Cyan': '#00bcd4',
                        'Teal': '#009688',
                        'Green': '#4caf50',
                        'Light Green': '#8bc34a',
                        'Lime': '#cddc39',
                        'Yellow': '#fdd835',
                        'Amber': '#ffc107',
                        'Orange': '#ff9800',
                        'Deep Orange': '#ff5722',
                        'Brown': '#795548',
                        'Grey': '#9e9e9e',
                        'Blue Grey': '#607d8b'
                      };
                      return colorMap[colorName] || '#667eea';
                    }
                    
                    // Final fallback
                    return '#667eea';
                  })();
                  
                  const categoryIdString = categoryId ? categoryId.toString() : '';
                  const isSelected = selectedCategory === categoryIdString;
                  const isHovered = hoveredCategory === categoryIdString;

                  return (
                    <MotionDiv
                      key={categoryId}
                      initial={{ opacity: 0, scale: 0.7, y: 30 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.7, y: -30 }}
                      transition={{ 
                        duration: 0.4, 
                        delay: 0.15 + (index * 0.08),
                        type: "spring",
                        stiffness: 120,
                        damping: 12
                      }}
                      whileHover={{ y: -3 }}
                    >
                      <Tooltip 
                        title={categoryName} 
                        placement="right"
                        arrow
                        TransitionComponent={Fade}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'rgba(0, 0, 0, 0.9)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: 2,
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'rgba(0, 0, 0, 0.9)',
                            }
                          }
                        }}
                      >
                        <ListItem 
                          component="div"
                          selected={isSelected}
                          onClick={() => onCategoryChange(categoryIdString)}
                          onMouseEnter={() => setHoveredCategory(categoryIdString)}
                          onMouseLeave={() => setHoveredCategory(null)}
                          sx={{ 
                            borderRadius: 3,
                            mb: isTabletSmall ? 0.75 : 1.25,
                            flexDirection: 'column',
                            p: isTabletSmall ? 1 : 1.5,
                            cursor: 'pointer',
                            minHeight: isTabletSmall ? 60 : 75,
                            position: 'relative',
                            overflow: 'hidden',
                            background: isSelected 
                              ? `linear-gradient(135deg, ${categoryColor}25 0%, ${categoryColor}15 100%)`
                              : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(20px)',
                            border: isSelected
                              ? `2px solid ${categoryColor}60`
                              : '1px solid rgba(0, 0, 0, 0.06)',
                            boxShadow: isSelected
                              ? `0 8px 32px ${categoryColor}30, inset 0 1px 0 rgba(255, 255, 255, 0.2)`
                              : isHovered
                                ? '0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                                : '0 4px 12px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              transform: 'translateY(-3px) scale(1.02)',
                              background: isSelected
                                ? `linear-gradient(135deg, ${categoryColor}30 0%, ${categoryColor}20 100%)`
                                : `linear-gradient(135deg, ${categoryColor}10 0%, rgba(255, 255, 255, 0.9) 100%)`,
                              borderColor: `${categoryColor}50`,
                              boxShadow: `0 12px 40px ${categoryColor}35, inset 0 1px 0 rgba(255, 255, 255, 0.4)`
                            },
                            '&:active': {
                              transform: 'translateY(-1px) scale(0.98)'
                            },
                            '&::before': isSelected ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '3px',
                              background: `linear-gradient(90deg, ${categoryColor}, ${categoryColor}CC, ${categoryColor})`,
                              opacity: 0.9
                            } : {},
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '200%',
                              height: '200%',
                              background: `radial-gradient(circle, ${categoryColor}08 0%, transparent 70%)`,
                              opacity: isHovered ? 1 : 0,
                              transition: 'opacity 0.3s ease',
                              pointerEvents: 'none'
                            }
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 'auto', mb: 0.75 }}>
                            <MotionDiv
                              whileHover={{ scale: 1.15, rotate: 8 }}
                              whileTap={{ scale: 0.9 }}
                              transition={{ type: "spring", stiffness: 200, damping: 10 }}
                            >
                              <Avatar 
                                sx={{ 
                                  bgcolor: categoryColor, // ✅ Use the properly extracted category color
                                  width: isTabletSmall ? 28 : 36,
                                  height: isTabletSmall ? 28 : 36,
                                  border: isSelected
                                    ? `2px solid rgba(255, 255, 255, 0.6)`
                                    : `1px solid ${categoryColor}30`,
                                  boxShadow: isSelected
                                    ? `0 8px 24px ${categoryColor}40, inset 0 1px 0 rgba(255, 255, 255, 0.3)`
                                    : `0 4px 16px ${categoryColor}20`,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '& .MuiSvgIcon-root': { 
                                    fontSize: isTabletSmall ? 16 : 18,
                                    color: 'white', // ✅ White icons on colored background
                                    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
                                  }
                                }}
                              >
                                {/* ✅ FIXED: Use the same icon resolution as CategoryList */}
                                {getIconByName(category.icon)}
                              </Avatar>
                            </MotionDiv>
                          </ListItemAvatar>
                          <Box sx={{ 
                            color: isSelected ? categoryColor : 'text.primary',
                            fontWeight: isSelected ? 700 : 600
                          }}>
                            {formatCategoryName(categoryName)}
                          </Box>
                        </ListItem>
                      </Tooltip>
                    </MotionDiv>
                  );
                })}
            </AnimatePresence>
          </List>
        </Box>
      )}
    </Paper>
  );
};

export default CategoriesSidebar;